import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { TableCellsIcon, Squares2X2Icon } from '@heroicons/react/24/outline';
import { TanTable } from './TanTable';
import EdiResultCard from './EdiResultCard';
import { FieldHighlightProvider } from './ProResultCard';
import EdiInfoModal from '../pages/pro/components/EdiInfoModal';
import EdiLocationsEditDrawer from './tablemaint/EdiLocationsEditDrawer';
import EdiEquipmentMapEditDrawer from './tablemaint/EdiEquipmentMapEditDrawer';
import EdiScacMapEditDrawer from './tablemaint/EdiScacMapEditDrawer';
import PortMapDrawer from './PortMapDrawer';
import ReasonPromptModal from './ReasonPromptModal';
import { WarningConfirmModal } from './ui/WarningConfirmModal';
import AlertModal from './ui/AlertModal';
import EdiTemplatePickerModal, { EdiTemplatePickerParams } from './EdiTemplatePickerModal';
import EdiAttachModal from './EdiAttachModal';
import EdiCreateModal from './EdiCreateModal';
import EdiCreateAllModal from './EdiCreateAllModal';
import type { BulkAction } from './TableActionBar';
import { getEdiLocation, EdiLocationRow } from '../services/edilocations.service';
import {
  EdiResultRow,
  getEdiBucketResults,
  acceptEdiOrders,
  rejectEdiOrders,
  deleteEdiOrders,
  clearAcceptEdiOrders,
  attachEdiOrder,
  EdiCreateAllResult,
} from '../services/pendingEdiOrders.service';
import { openDupeProSearch } from '../utils/ediDupes';
import { openProSearch } from '../utils/proSearch';
import { urgencyRowClass } from '../utils/ediUrgency';
import useEdiOrdersFieldDefinitions from '../hooks/useEdiOrdersFieldDefinitions';
import { useVisibilityAwareInterval } from '../hooks/useVisibilityAwareInterval';
import {
  buildEdiColumns,
  buildEdiDefaultVisibility,
  buildEdiCardDefaultVisibility,
  buildEdiAvailableFields,
  canGetTemplate,
  canCreate,
} from '../utils/ediOrderColumns';

// =============================================================================
// EdiBucketDrill
// =============================================================================
// The drill-down for ONE pending EDI order bucket (edihandle × Bill To): the
// single-row TanTable or multi-row card list, the per-record + table-wide
// Accept/Reject/Delete/Clear actions, and the four mapping editors (EDI Info,
// Location, Size/Type, Port, SCAC). Extracted from the Pending EDI Orders page so
// the SAME drill can be embedded elsewhere (the LC Dashboard opens it in a drawer,
// staying in context) without duplicating any of this machinery.
//
// It owns its own results fetch + background refresh; the host owns the bucket list.
// After a data-changing action it calls onActionApplied so the host can refresh its
// bucket counts (the drill refreshes its own rows). The optional `header` renders to
// the left of the view toggle (the page uses it for the "Back to buckets" bar).

const LS_RESULTS = 'pending-edi-results-view';
// Bump on default-column changes so saved layouts reset to the new defaults instead of
// pinning the old set. v2: editype pinned + dupe count. v3: Port of Origin / Discharge.
// v4: Accepted By + EDI Accept Date + right-pinned Actions column.
const RESULTS_SETTINGS_KEY = 'pending-edi-orders-results-v4';
// Card (Multi Row) view keeps its OWN saved column layout, separate from the table view's,
// so the wide card field set can't widen the table (John's lc-cards / lc-card split).
const CARDS_SETTINGS_KEY = 'pending-edi-orders-cards-v1';
// Columns the card draws in its own strip (the per-record actions), so they're excluded from
// the card's field grid. Module-level for a stable reference (TanTable memoizes on it).
const EDI_CARD_HEADER_COLUMN_IDS = ['__actions'];
// Background refresh cadence (matches the LC Dashboard counts). Visibility-aware:
// pauses when the tab is hidden and fires immediately on resume.
const REFRESH_MS = 30_000;

type ResultsView = 'table' | 'cards';

export interface EdiDrillSelection {
  edihandle: string;
  billto: number;
  label?: string;
}

// Drill signature: which pending orders are present + their pro/accept state (captures
// moves coming in and being accepted — the changes worth re-rendering for).
const rowsSignature = (rows: EdiResultRow[]): string =>
  rows.map((r) => `${r.recno}:${r.pronumber}:${r.acceptreject}`).join('|');

export interface EdiBucketDrillProps {
  /** The bucket to drill into. When null the drill renders nothing. */
  selection: EdiDrillSelection | null;
  /** Called after an action changes data (accept/reject/delete/clear) so the host can
   *  refresh its bucket counts — the drill refreshes its own rows itself. */
  onActionApplied?: () => void;
  /** Optional content rendered to the left of the view toggle (e.g. a "Back" bar). */
  header?: React.ReactNode;
  /** Fill the host's box (h-full flex column, table sized by the container) instead of
   *  the standalone page's intrinsic h-[calc(100vh-280px)]. Hosts that give the drill a
   *  flex-1/min-h-0 parent — the LC Dashboard drill + drawer — pass this so the table
   *  ends exactly where the box does and the box never grows its own scrollbar. */
  fillContainer?: boolean;
  /** LC scope for the rows, which MUST be the same scope the host used to build the
   *  bucket it is drilling into — otherwise the drill lists orders the bucket did not
   *  count. Undefined/empty = unscoped (every order in the bucket).
   *
   *  This also scopes the batch actions, since Accept / Reject / Delete / Get Template
   *  For All / Create All all post explicit recnos taken from these rows. */
  lcUsernames?: string[];
}

/**
 * The pros named in the attached/created strips, in the order the strips show them.
 *
 * Deduped: two EDI orders can be attached to the SAME pro, and repeating a term makes
 * the search results list that pro twice. Blanks AND a literal 0 are dropped — 0 is this
 * board's "no pro yet" sentinel (the pending fetch keeps rows on `pronumber != 0`), so a
 * row still carrying it has nothing to search for, and passing it as a term would send
 * the user to a results page for a pro that does not exist.
 */
export function bannerProNumbers(
  attached: ReadonlyArray<{ pronumber: string | number }>,
  created: ReadonlyArray<{ pronumber: string | number }>,
): string[] {
  const all = [...attached, ...created].map((r) => String(r.pronumber ?? '').trim());
  return [...new Set(all.filter((p) => p && p !== '0'))];
}

const EdiBucketDrill: React.FC<EdiBucketDrillProps> = ({ selection, onActionApplied, header, fillContainer, lcUsernames }) => {
  const [rows, setRows] = useState<EdiResultRow[]>([]);
  const [rowsLoading, setRowsLoading] = useState(false);
  const [rowsError, setRowsError] = useState<string | null>(null);

  // Hosts pass lcUsernames as a fresh array each render. loadResults depends on it and
  // an effect depends on loadResults, so keying the memo on the JOINED value — not the
  // array identity — is what stops the drill reloading itself in a loop.
  const lcKey = (lcUsernames || []).join(',');
  const lcScope = useMemo(() => (lcKey ? lcKey.split(',') : []), [lcKey]);

  const [resultsView, setResultsView] = useState<ResultsView>(
    () => (localStorage.getItem(LS_RESULTS) === 'cards' ? 'cards' : 'table')
  );
  const setResults = (v: ResultsView) => {
    setResultsView(v);
    localStorage.setItem(LS_RESULTS, v);
  };

  // EDI Info modal, opened by clicking a row's EDI type (card or table).
  const [ediInfoRecno, setEdiInfoRecno] = useState<number | null>(null);
  const openEdiInfo = useCallback((recno: number) => setEdiInfoRecno(recno), []);

  // Location mapping: clicking an unmapped location's "Map Location" fetches the full
  // edi_locations row and opens the existing tablemaint edit drawer.
  const [mapLocationRow, setMapLocationRow] = useState<EdiLocationRow | null>(null);
  const openMapLocation = useCallback((recno: number) => {
    getEdiLocation(recno)
      .then(setMapLocationRow)
      .catch((e) => setRowsError(e?.message || 'Failed to load EDI location.'));
  }, []);

  // Size/Type mapping: opens the edi_equipmentmap editor for that recno.
  const [mapEquipmentRecno, setMapEquipmentRecno] = useState<number | null>(null);
  const openMapEquipment = useCallback((recno: number) => setMapEquipmentRecno(recno), []);

  // Port mapping: opens the port-map drawer for that EDI unlocode (V1 edi_mapport). Keyed
  // by the unlocode string (ports map by stamping the unlocode onto a cms.ports row).
  const [mapPortUnlocode, setMapPortUnlocode] = useState<string | null>(null);
  const openMapPort = useCallback((unlocode: string) => setMapPortUnlocode(unlocode), []);

  // SCAC mapping: opens the edi_scacmap editor for that recno (an SS-line text → CMS SCAC).
  const [mapScacRecno, setMapScacRecno] = useState<number | null>(null);
  const openMapScac = useCallback((recno: number) => setMapScacRecno(recno), []);

  const { fieldDefinitions, loading: fieldsLoading } = useEdiOrdersFieldDefinitions();

  const defaultColumnVisibility = useMemo(
    () => buildEdiDefaultVisibility(fieldDefinitions),
    [fieldDefinitions]
  );
  const cardDefaultVisibility = useMemo(
    () => buildEdiCardDefaultVisibility(fieldDefinitions),
    [fieldDefinitions]
  );
  const availableFields = useMemo(() => buildEdiAvailableFields(fieldDefinitions), [fieldDefinitions]);
  // Field id -> registry type, so the card can format dates / numbers (the table cells do
  // this via cellFor; card mode renders values itself).
  const fieldTypeById = useMemo(
    () => new Map(fieldDefinitions.map((f) => [(f.source_fieldname || f.fieldname || '').toString(), f.type as string])),
    [fieldDefinitions]
  );

  // `force` bypasses the signature guard — used after a mapping saves, since that changes
  // a name (not recno/pro/accept), which the signature wouldn't detect.
  const loadResults = useCallback((sel: EdiDrillSelection | null, silent = false, force = false) => {
    if (!sel) {
      setRows([]);
      return Promise.resolve();
    }
    if (!silent) {
      setRowsLoading(true);
      setRowsError(null);
    }
    return getEdiBucketResults(sel.edihandle, sel.billto, lcScope)
      .then((resp) => setRows((prev) => (!force && rowsSignature(prev) === rowsSignature(resp.data) ? prev : resp.data)))
      .catch((e) => {
        if (!silent) setRowsError(e?.message || 'Failed to load results.');
      })
      .finally(() => {
        if (!silent) setRowsLoading(false);
      });
  }, [lcScope]);

  // Reload the drill whenever the selected bucket changes.
  useEffect(() => {
    loadResults(selection, false);
  }, [selection, loadResults]);

  // Background refresh: keep the open drill current so moves that come in (or get
  // accepted elsewhere) appear without a manual reload. Silent so it doesn't flash.
  useVisibilityAwareInterval(() => {
    if (selection) loadResults(selection, true);
  }, REFRESH_MS);

  // --- per-record + table-level actions (Accept / Reject / Delete / Clear) ---
  // Reject/Delete need a reason (single modal for both single-record and "…All"); Accept and
  // Clear just confirm. Accept & Reject only apply to blank rows — an already-accepted row is
  // skipped, and the "…All" variants warn how many will be skipped before running.
  const [reasonModal, setReasonModal] =
    useState<{ action: 'reject' | 'delete'; recnos: number[]; note?: string } | null>(null);
  const [confirmModal, setConfirmModal] =
    useState<{ title: string; message: React.ReactNode; confirmLabel: string; run: () => void } | null>(null);
  const [alertModal, setAlertModal] = useState<{ title: string; message: React.ReactNode } | null>(null);
  const [actionBusy, setActionBusy] = useState(false);

  const refreshAfterAction = useCallback(() => {
    onActionApplied?.(); // host refreshes its bucket counts
    return loadResults(selection, true, true); // force: names/state change, not just recno set
  }, [onActionApplied, loadResults, selection]);

  const runAccept = useCallback((recnos: number[]) => {
    acceptEdiOrders(recnos).then(refreshAfterAction).catch((e) => setRowsError(e?.message || 'Accept failed.'));
  }, [refreshAfterAction]);
  const runClear = useCallback((recnos: number[]) => {
    clearAcceptEdiOrders(recnos).then(refreshAfterAction).catch((e) => setRowsError(e?.message || 'Clear failed.'));
  }, [refreshAfterAction]);

  const blankRecnos = (rs: EdiResultRow[]): number[] =>
    rs.filter((r) => !((r.acceptreject as string) || '')).map((r) => Number(r.recno));

  // --- per-record (single) — all four confirm first (Accept/Clear via confirm modal;
  //     Reject/Delete via the reason modal, which is itself the confirmation step) ---
  const acceptOne = useCallback((recno: number) => {
    setConfirmModal({ title: 'Accept EDI Order', message: 'Accept this EDI order?', confirmLabel: 'Accept', run: () => runAccept([recno]) });
  }, [runAccept]);
  const clearOne = useCallback((recno: number) => {
    setConfirmModal({ title: 'Clear Accept/Reject', message: 'Clear the accept/reject on this EDI order? It becomes un-processed again.', confirmLabel: 'Clear', run: () => runClear([recno]) });
  }, [runClear]);
  const rejectOne = useCallback((recno: number) => setReasonModal({ action: 'reject', recnos: [recno] }), []);
  const deleteOne = useCallback((recno: number) => setReasonModal({ action: 'delete', recnos: [recno] }), []);

  // --- table-wide / card-header (…All) over the given (visible) rows ---
  const acceptAll = useCallback((rs: EdiResultRow[]) => {
    const eligible = blankRecnos(rs);
    const skipped = rs.length - eligible.length;
    if (!eligible.length) { setAlertModal({ title: 'Nothing to accept', message: 'None of the EDI orders in view are un-processed.' }); return; }
    setConfirmModal({
      title: 'Accept EDI Orders',
      message: skipped
        ? `${skipped} of ${rs.length} order(s) in view are already accepted and will be skipped. Accept the ${eligible.length} un-processed order(s)?`
        : `Accept ${eligible.length} EDI order(s) in view?`,
      confirmLabel: 'Accept', run: () => runAccept(eligible),
    });
  }, [runAccept]);

  const rejectAll = useCallback((rs: EdiResultRow[]) => {
    const eligible = blankRecnos(rs);
    const skipped = rs.length - eligible.length;
    if (!eligible.length) { setAlertModal({ title: 'Nothing to reject', message: 'None of the EDI orders in view are un-processed.' }); return; }
    setReasonModal({
      action: 'reject', recnos: eligible,
      note: skipped ? `${skipped} of ${rs.length} order(s) in view are already accepted and will be skipped.` : undefined,
    });
  }, []);

  const deleteAll = useCallback((rs: EdiResultRow[]) => {
    const recnos = rs.map((r) => Number(r.recno));
    if (!recnos.length) { setAlertModal({ title: 'Nothing to delete', message: 'There are no EDI orders in view.' }); return; }
    setReasonModal({ action: 'delete', recnos });
  }, []);

  const submitReason = useCallback((reason: string) => {
    if (!reasonModal) return;
    const { action, recnos } = reasonModal;
    setActionBusy(true);
    const call = action === 'reject' ? rejectEdiOrders(recnos, reason) : deleteEdiOrders(recnos, reason);
    call
      .then(() => refreshAfterAction())
      .then(() => { setActionBusy(false); setReasonModal(null); })
      .catch((e) => { setActionBusy(false); setReasonModal(null); setRowsError(e?.message || `${action} failed.`); });
  }, [reasonModal, refreshAfterAction]);

  // --- Get Template (single) + Get Template For All (bucket-shared) — same picker + apply ---
  const [templatePicker, setTemplatePicker] =
    useState<{ params: EdiTemplatePickerParams; recnos: number[]; title: string; onlyBlank: boolean; currentTemplate?: string } | null>(null);

  // One handler for both the "Get Template" action (blank row) and the Template-column link
  // (set row → change / clear). only_blank=false so a single edit may overwrite/clear; the
  // picker shows "Clear Template" when the row already has one (currentTemplate).
  const openTemplateSingle = useCallback((recno: number) => {
    const row = rows.find((r) => Number(r.recno) === Number(recno));
    if (!row || !selection) return;
    setTemplatePicker({
      params: {
        edihandle: selection.edihandle,
        billto: Number(row.billto) || selection.billto,
        movetype: (row.movetype as string) || '',
        deliveryrecno: Number(row.deliveryrecno) || 0,
      },
      recnos: [Number(recno)],
      title: `${selection.label || selection.edihandle} · order #${recno}`,
      onlyBlank: false,
      currentTemplate: ((row.template as string) || '').trim(),
    });
  }, [rows, selection]);

  // "…For All" requires every visible row to share Bill To + Move Type + mapped delivery
  // recno (Lee) — then the picker (fed those shared params) stamps the blank-template rows.
  const getTemplateAll = useCallback((rs: EdiResultRow[]) => {
    if (!selection || !rs.length) { setAlertModal({ title: 'Nothing to template', message: 'There are no EDI orders in view.' }); return; }
    const first = rs[0];
    const dr = Number(first.deliveryrecno) || 0;
    const shared = dr > 0 && rs.every((r) =>
      String(r.billto) === String(first.billto) &&
      String((r.movetype as string) || '') === String((first.movetype as string) || '') &&
      (Number(r.deliveryrecno) || 0) === dr);
    if (!shared) {
      setAlertModal({
        title: 'Can’t Get Template for all',
        message: 'Get Template For All needs every order in view to share the same Bill To, Move Type, and mapped delivery location. Filter the view to a matching set and try again.',
      });
      return;
    }
    const eligible = rs.filter((r) => canGetTemplate(r)).map((r) => Number(r.recno));
    if (!eligible.length) { setAlertModal({ title: 'Nothing to template', message: 'Every order in view already has a template.' }); return; }
    setTemplatePicker({
      params: { edihandle: selection.edihandle, billto: Number(first.billto) || selection.billto, movetype: (first.movetype as string) || '', deliveryrecno: dr },
      recnos: eligible,
      title: `${selection.label || selection.edihandle} · ${eligible.length} order(s)`,
      onlyBlank: true, // "…For All" stamps only blank-template rows
    });
  }, [selection]);

  // --- Attach to an existing pro (V1 edi_attachpro) — candidate list + free entry. The
  // eligibility gate (accepted + no missing product) lives in canAttach; the attach-save
  // itself is a later step, so onChosen just surfaces the picked pronumber for now. ---
  const [attachModal, setAttachModal] = useState<{ recno: number; title: string } | null>(null);
  const openAttach = useCallback((recno: number) => {
    if (!selection) return;
    setAttachModal({ recno, title: `${selection.label || selection.edihandle} · order #${recno}` });
  }, [selection]);

  // Just-attached orders: the pending fetch drops them (pronumber != 0), so we KEEP a local
  // strip of them — openable + dismissable — until the user dismisses or reloads the page
  // (Lee: make "open the pro" and "move on" both available). Persists across the 30s refresh;
  // resets on a real page reload (component unmount).
  const [attachedRows, setAttachedRows] = useState<{ recno: number; pronumber: string }[]>([]);
  const runAttach = useCallback((recno: number, pronumber: string, overrideFields: string[]) => {
    setActionBusy(true);
    attachEdiOrder(recno, pronumber, overrideFields)
      .then(() => {
        setAttachedRows((prev) => [{ recno, pronumber }, ...prev.filter((r) => r.recno !== recno)]);
        return refreshAfterAction();
      })
      .then(() => setActionBusy(false))
      .catch((e) => {
        setActionBusy(false);
        setAlertModal({ title: 'Attach failed', message: e?.response?.data?.detail || e?.message || 'The attach could not be completed.' });
      });
  }, [refreshAfterAction]);

  // --- Create a NEW pro from an accepted+templated EDI order (V1 edi_createpro). The
  // eligibility gate (accepted + valid template + no missing product) lives in canCreate;
  // ports-mapped + IMPORT-ETA are enforced server-side and surfaced in the dialog. ---
  const [createModal, setCreateModal] = useState<{ recno: number; title: string } | null>(null);
  const openCreate = useCallback((recno: number) => {
    if (!selection) return;
    setCreateModal({ recno, title: `${selection.label || selection.edihandle} · order #${recno}` });
  }, [selection]);

  // Just-created orders: the pending fetch drops them (pronumber != 0), so keep a local strip
  // — openable + dismissable — until dismissed or a page reload (mirrors attachedRows).
  const [createdRows, setCreatedRows] = useState<{ recno: number; pronumber: number }[]>([]);
  const onCreated = useCallback((res: { recno: number; pronumber: number }) => {
    setCreateModal(null);
    setCreatedRows((prev) => [{ recno: res.recno, pronumber: res.pronumber }, ...prev.filter((r) => r.recno !== res.recno)]);
    refreshAfterAction();
  }, [refreshAfterAction]);

  // Create All (V1 edi_createallorders): the faithful multi-row form over the visible eligible
  // rows. Ineligible rows are filtered out here; the server additionally skips port/ETA cases.
  const [createAllModal, setCreateAllModal] = useState<{ recnos: number[] } | null>(null);
  const createAll = useCallback((rs: EdiResultRow[]) => {
    const eligible = rs.filter((r) => canCreate(r)).map((r) => Number(r.recno));
    if (!eligible.length) {
      setAlertModal({ title: 'Nothing to create', message: 'No orders in view are eligible to create (need accepted + valid template + no missing product).' });
      return;
    }
    setCreateAllModal({ recnos: eligible });
  }, []);
  const onCreateAllDone = useCallback((res: EdiCreateAllResult) => {
    setCreateAllModal(null);
    if (res.created.length) {
      setCreatedRows((prev) => [
        ...res.created.map((c) => ({ recno: c.recno, pronumber: c.pronumber })),
        ...prev.filter((p) => !res.created.some((c) => c.recno === p.recno)),
      ]);
    }
    refreshAfterAction();
    if (res.skipped_count > 0) {
      setAlertModal({
        title: `Created ${res.created_count}, skipped ${res.skipped_count}`,
        message: res.skipped.map((s) => `#${s.recno}: ${s.reason}`).join('\n') || 'Some orders were skipped.',
      });
    }
  }, [refreshAfterAction]);

  const bannerPros = useMemo(
    () => bannerProNumbers(attachedRows, createdRows),
    [attachedRows, createdRows],
  );

  const columns = useMemo(
    () => buildEdiColumns(fieldDefinitions, {
      onEdiInfoClick: openEdiInfo, onMapLocation: openMapLocation, onMapEquipment: openMapEquipment,
      onMapPort: openMapPort, onMapScac: openMapScac,
      onAcceptRow: acceptOne, onRejectRow: rejectOne, onDeleteRow: deleteOne, onClearRow: clearOne,
      onGetTemplateRow: openTemplateSingle, onAttachRow: openAttach, onCreateRow: openCreate,
      onEditTemplate: openTemplateSingle,
    }),
    [fieldDefinitions, openEdiInfo, openMapLocation, openMapEquipment, openMapPort, openMapScac,
     acceptOne, rejectOne, deleteOne, clearOne, openTemplateSingle, openAttach, openCreate]
  );

  // Card (Multi Row) renderer for TanTable card mode: the field grid is driven by the ordered
  // visible columns (ctx.visibleColumns), so column show/hide/reorder/filter shape the card.
  const renderEdiCard = useCallback(
    (row: EdiResultRow, ctx: { visibleColumns: { id: string; label: string }[] }) => (
      <EdiResultCard
        row={row}
        fields={ctx.visibleColumns}
        types={fieldTypeById}
        onEdiInfoClick={openEdiInfo}
        onMapLocation={openMapLocation}
        onMapEquipment={openMapEquipment}
        onMapPort={openMapPort}
        onMapScac={openMapScac}
        onAccept={acceptOne}
        onReject={rejectOne}
        onDelete={deleteOne}
        onClear={clearOne}
        onGetTemplate={openTemplateSingle}
        onAttach={openAttach}
        onCreate={openCreate}
        onEditTemplate={openTemplateSingle}
      />
    ),
    [fieldTypeById, openEdiInfo, openMapLocation, openMapEquipment, openMapPort, openMapScac,
     acceptOne, rejectOne, deleteOne, clearOne, openTemplateSingle, openAttach, openCreate]
  );

  // Table-level mass actions (TanTable action bar) — scoped to the visible/filtered rows.
  const PILL = 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium text-white disabled:opacity-50';
  const bulkActions = useMemo<BulkAction[]>(() => [
    { key: 'create-all', label: 'Create All', onRun: (rs) => createAll(rs as EdiResultRow[]),
      className: `${PILL} bg-emerald-600 hover:bg-emerald-700` },
    { key: 'get-template-all', label: 'Get Template All', onRun: (rs) => getTemplateAll(rs as EdiResultRow[]),
      className: `${PILL} bg-indigo-600 hover:bg-indigo-700` },
    { key: 'accept-all', label: 'Accept All', onRun: (rs) => acceptAll(rs as EdiResultRow[]),
      className: `${PILL} bg-green-600 hover:bg-green-700` },
    { key: 'reject-all', label: 'Reject All', onRun: (rs) => rejectAll(rs as EdiResultRow[]),
      className: `${PILL} bg-amber-600 hover:bg-amber-700` },
    { key: 'delete-all', label: 'Delete All', onRun: (rs) => deleteAll(rs as EdiResultRow[]),
      className: `${PILL} bg-red-600 hover:bg-red-700` },
  ], [createAll, getTemplateAll, acceptAll, rejectAll, deleteAll]);

  // --- view toggle (single row / multi row) ---
  const viewToggle = (
    <div className="inline-flex rounded-md border border-gray-300 dark:border-gray-600 overflow-hidden">
      <button
        type="button"
        onClick={() => setResults('cards')}
        className={`flex items-center gap-1 px-2.5 py-1 text-xs font-medium ${
          resultsView === 'cards'
            ? 'bg-blue-600 text-white'
            : 'bg-panel text-gray-700 dark:text-gray-200 hover:bg-highlight'
        }`}
      >
        <Squares2X2Icon className="w-4 h-4" /> Multi Row
      </button>
      <button
        type="button"
        onClick={() => setResults('table')}
        className={`flex items-center gap-1 px-2.5 py-1 text-xs font-medium ${
          resultsView === 'table'
            ? 'bg-blue-600 text-white'
            : 'bg-panel text-gray-700 dark:text-gray-200 hover:bg-highlight'
        }`}
      >
        <TableCellsIcon className="w-4 h-4" /> Single Row
      </button>
    </div>
  );

  // ONE TanTable for both views: Single Row = the grid, Multi Row = card mode (renderCardRow).
  // Card mode renders each row via EdiResultCard over the ordered visible columns, so column
  // show/hide/reorder/filter + the mass-action bar work in BOTH views. FieldHighlightProvider
  // gives the cards their click-to-highlight; key={resultsView} remounts on toggle so the
  // right saved layout loads; each view keeps its own settingsKey + default visibility.
  //
  // Hold the first mount until the column registry has loaded: TanTable reads
  // defaultColumnVisibility only on mount (per settingsKey/key), and buildEdi*DefaultVisibility
  // is empty until fieldDefinitions arrive — mounting early would show EVERY column (TanTable
  // treats unlisted columns as visible) until a view-toggle remounted it. The defs are
  // module-cached, so this gate only ever shows on the first cold visit.
  const isCards = resultsView === 'cards';
  const drillBody = (fieldsLoading && fieldDefinitions.length === 0) ? (
    <div className={`${fillContainer ? 'h-full' : 'h-[calc(100vh-280px)]'} flex items-center justify-center text-sm text-muted`}>
      Loading columns…
    </div>
  ) : (
    <FieldHighlightProvider>
      <TanTable<EdiResultRow>
        key={resultsView}
        data={rows}
        columns={columns as ColumnDef<EdiResultRow>[]}
        loading={rowsLoading}
        errorMessage={rowsError}
        renderCardRow={isCards ? renderEdiCard : undefined}
        cardHeaderColumnIds={EDI_CARD_HEADER_COLUMN_IDS}
        settingsKey={isCards ? CARDS_SETTINGS_KEY : RESULTS_SETTINGS_KEY}
        fieldName={isCards ? CARDS_SETTINGS_KEY : RESULTS_SETTINGS_KEY}
        cardTitle={selection?.label || 'Results'}
        getRowClassName={(row) => urgencyRowClass((row as EdiResultRow).urgency)}
        defaultColumnVisibility={isCards ? cardDefaultVisibility : defaultColumnVisibility}
        availableFields={availableFields}
        enableColumnFilters
        enableGrouping={!isCards}
        enableExcelExport
        bulkActions={bulkActions}
        fillContainer={fillContainer}
        lockedColumns={isCards ? undefined : [{ id: 'editype', side: 'left' }, { id: '__template', side: 'left' }, { id: '__actions', side: 'right' }]}
      />
    </FieldHighlightProvider>
  );

  if (!selection) return null;

  return (
    <div className={fillContainer ? 'h-full min-h-0 flex flex-col gap-2' : 'space-y-2'}>
      <div className="flex items-center gap-3 flex-shrink-0">
        {header}
        <div className="ml-auto">{viewToggle}</div>
      </div>

      {/* One link to all of them. The strips give a pro each, one tab at a time; after a
          Create All that is the difference between 30 tabs and one search page. Spans
          BOTH strips because "what did I just do" doesn't distinguish created from
          linked. Dismissing a row drops its pro from here too. */}
      {bannerPros.length > 0 && (
        <div className="flex-shrink-0">
          <button
            type="button"
            onClick={() => openProSearch(bannerPros)}
            className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline"
            title="Open search results for every pro listed below, in a new tab"
          >
            View all {bannerPros.length} {bannerPros.length === 1 ? 'order' : 'orders'} in search results ↗
          </button>
        </div>
      )}

      {/* Just-attached orders — kept openable/dismissable until dismissed or a page reload.
          The live list already dropped them (pronumber != 0), so this is the only place to
          still reach the pro (or confirm the attach and move on). */}
      {attachedRows.length > 0 && (
        <div className="flex-shrink-0 rounded-md border border-green-300 bg-green-50 dark:border-green-700 dark:bg-green-900/20 px-3 py-2">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-semibold uppercase tracking-wide text-green-700 dark:text-green-400">
              Attached ({attachedRows.length})
            </span>
            <button type="button" onClick={() => setAttachedRows([])}
              className="text-xs text-green-700 dark:text-green-400 hover:underline">Dismiss all</button>
          </div>
          <ul className="space-y-0.5">
            {attachedRows.map((a) => (
              <li key={a.recno} className="flex items-center gap-2 text-sm text-slate-700 dark:text-gray-200">
                <span>Order #{a.recno} →</span>
                <button type="button" onClick={() => openDupeProSearch([a.pronumber])}
                  className="font-medium text-blue-600 dark:text-blue-400 hover:underline tabular-nums"
                  title={`Open pro ${a.pronumber}`}>pro {a.pronumber}</button>
                <button type="button" onClick={() => setAttachedRows((prev) => prev.filter((r) => r.recno !== a.recno))}
                  className="ml-auto text-xs text-slate-500 dark:text-gray-400 hover:text-slate-700 dark:hover:text-gray-200 hover:underline">
                  Dismiss
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Just-created orders — kept openable/dismissable until dismissed or a page reload
          (the live fetch drops them once pronumber != 0). Mirrors the Attached strip above. */}
      {createdRows.length > 0 && (
        <div className="flex-shrink-0 rounded-md border border-emerald-300 bg-emerald-50 dark:border-emerald-700 dark:bg-emerald-900/20 px-3 py-2">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-400">
              Created ({createdRows.length})
            </span>
            <button type="button" onClick={() => setCreatedRows([])}
              className="text-xs text-emerald-700 dark:text-emerald-400 hover:underline">Dismiss all</button>
          </div>
          <ul className="space-y-0.5">
            {createdRows.map((c) => (
              <li key={c.recno} className="flex items-center gap-2 text-sm text-slate-700 dark:text-gray-200">
                <span>Order #{c.recno} →</span>
                <button type="button" onClick={() => openDupeProSearch([String(c.pronumber)])}
                  className="font-medium text-blue-600 dark:text-blue-400 hover:underline tabular-nums"
                  title={`Open pro ${c.pronumber}`}>pro {c.pronumber}</button>
                <button type="button" onClick={() => setCreatedRows((prev) => prev.filter((r) => r.recno !== c.recno))}
                  className="ml-auto text-xs text-slate-500 dark:text-gray-400 hover:text-slate-700 dark:hover:text-gray-200 hover:underline">
                  Dismiss
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className={fillContainer ? 'flex-1 min-h-0' : undefined}>{drillBody}</div>

      {/* EDI Info modal — opened by clicking a row's EDI type (card or table). */}
      <EdiInfoModal
        open={ediInfoRecno != null}
        recno={ediInfoRecno ?? undefined}
        onClose={() => setEdiInfoRecno(null)}
        onMappingSaved={() => loadResults(selection, true, true)}
      />

      {/* Location mapping — the existing tablemaint EDI Locations edit drawer, opened
          from a "Map Location" cell. On save, refresh the drill so mapped names appear. */}
      {mapLocationRow && (
        <EdiLocationsEditDrawer
          row={mapLocationRow}
          onClose={() => setMapLocationRow(null)}
          onSaved={() => loadResults(selection, true, true)}
        />
      )}

      {/* Size/Type mapping — the edi_equipmentmap editor, opened from a "Map Size/Type"
          cell. On save, refresh the drill so the combined size/type appears. */}
      {mapEquipmentRecno != null && (
        <EdiEquipmentMapEditDrawer
          recno={mapEquipmentRecno}
          onClose={() => setMapEquipmentRecno(null)}
          onSaved={() => loadResults(selection, true, true)}
        />
      )}

      {/* Port mapping — stamp an EDI unlocode onto a CMS port (V1 edi_mapport), opened from
          a "Map Port" cell. On save, refresh the drill so the mapped port name appears. */}
      {mapPortUnlocode && (
        <PortMapDrawer
          unlocode={mapPortUnlocode}
          onClose={() => setMapPortUnlocode(null)}
          onSaved={() => loadResults(selection, true, true)}
        />
      )}

      {/* SCAC mapping — the edi_scacmap editor, opened from a "Map SCAC" cell. On save,
          refresh the drill so the resolved SCAC appears. */}
      {mapScacRecno != null && (
        <EdiScacMapEditDrawer
          recno={mapScacRecno}
          onClose={() => setMapScacRecno(null)}
          onSaved={() => loadResults(selection, true, true)}
        />
      )}

      {/* Reject / Delete reason prompt (single record or "…All") — required free-text. */}
      {reasonModal && (
        <ReasonPromptModal
          title={reasonModal.action === 'reject'
            ? `Reject ${reasonModal.recnos.length} EDI order${reasonModal.recnos.length === 1 ? '' : 's'}`
            : `Delete ${reasonModal.recnos.length} EDI order${reasonModal.recnos.length === 1 ? '' : 's'}`}
          label={reasonModal.action === 'reject' ? 'Rejection reason (sent to the sender as a 990)' : 'Delete reason'}
          confirmLabel={reasonModal.action === 'reject' ? 'Reject' : 'Delete'}
          danger={reasonModal.action === 'delete'}
          note={reasonModal.note}
          saving={actionBusy}
          onCancel={() => { if (!actionBusy) setReasonModal(null); }}
          onConfirm={submitReason}
        />
      )}

      {/* Accept / Clear confirmation (Reject & Delete confirm via the reason modal above). */}
      {confirmModal && (
        <WarningConfirmModal
          open
          title={confirmModal.title}
          message={confirmModal.message}
          confirmLabel={confirmModal.confirmLabel}
          cancelLabel="Cancel"
          onConfirm={() => { const run = confirmModal.run; setConfirmModal(null); run(); }}
          onCancel={() => setConfirmModal(null)}
        />
      )}

      {/* Info modal for "nothing to do" cases (replaces window.alert). */}
      {alertModal && (
        <AlertModal open title={alertModal.title} message={alertModal.message} onClose={() => setAlertModal(null)} />
      )}

      {/* Get Template drop-down (single or "…For All") — picking a template stamps it. */}
      {templatePicker && (
        <EdiTemplatePickerModal
          params={templatePicker.params}
          recnos={templatePicker.recnos}
          title={templatePicker.title}
          currentTemplate={templatePicker.currentTemplate}
          onlyBlank={templatePicker.onlyBlank}
          onClose={() => setTemplatePicker(null)}
          onApplied={refreshAfterAction}
        />
      )}

      {/* Attach to an existing pro (V1 edi_attachpro) — the dupe-match candidate list + free
          entry. The chosen pronumber is surfaced via onChosen; the attach-save is a later
          step, so for now we confirm the selection and close. */}
      {attachModal && (
        <EdiAttachModal
          recno={attachModal.recno}
          title={attachModal.title}
          onClose={() => setAttachModal(null)}
          onChosen={(pro, overrideFields) => {
            const rec = attachModal.recno;
            setAttachModal(null);
            runAttach(rec, pro, overrideFields);
          }}
        />
      )}

      {/* Create a NEW pro from an accepted+templated EDI order (V1 edi_createpro). */}
      {createModal && (
        <EdiCreateModal
          recno={createModal.recno}
          title={createModal.title}
          onClose={() => setCreateModal(null)}
          onCreated={onCreated}
        />
      )}

      {/* Create All Orders — the faithful multi-row form (V1 edi_createallorders). */}
      {createAllModal && (
        <EdiCreateAllModal
          recnos={createAllModal.recnos}
          title={selection.label || selection.edihandle}
          onClose={() => setCreateAllModal(null)}
          onDone={onCreateAllDone}
        />
      )}
    </div>
  );
};

export default EdiBucketDrill;
